// /admin/templates/:id — a vertical template editor (mockup 47, view 2). Header
// + dimension tabs; only the "Reglas de sensibilidad" tab is built — the other
// dimension editors are shown as DISABLED tabs (an honest "not yet"), not
// clickable tabs over an empty panel. Each priority-trigger rule shows its
// detection phrases, example and action as READ-ONLY (they're non-negotiable),
// but the crisis/ER redirect `message` is editable. Publishing persists the
// edited messages immediately, bumps the template version and invalidates every
// inheriting bot's config cache (publishTemplate() seam, platform_admin+ only).
// Data via the getVerticalTemplate() seam.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  createVerticalTemplate,
  EDITOR_TABS,
  getVerticalTemplate,
  publishTemplate,
  renameVerticalTemplate,
  type SensitivityRule,
} from './templates-data.js'
import { VERTICAL_WRAP, VerticalIcon } from './templates-ui.js'
import {
  Button,
  Chip,
  DropdownMenu,
  EmptyStateCard,
  FormField,
  Input,
  Modal,
  PageHeader,
  Spinner,
  Tabs,
  TabPanel,
  Textarea,
  toast,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'

function RuleCard({
  rule,
  message,
  onMessageChange,
  disabled,
}: {
  rule: SensitivityRule
  message: string
  onMessageChange: (value: string) => void
  disabled: boolean
}): JSX.Element {
  const fieldId = `rule-message-${rule.id}`
  return (
    <li className="rounded-xl border border-line bg-surface p-4">
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-[13px] font-bold text-text">{rule.title}</h3>
            {/* Priority triggers are always-on by design — no toggle, just the
                non-negotiable marker. */}
            <Chip tone="warn">No negociable</Chip>
          </div>
          <p className="mt-1 text-[12px] leading-snug text-text-2">{rule.description}</p>
        </div>
      </div>

      {/* Detection phrases — read-only (the safety net is non-negotiable). */}
      <div className="mt-2.5 flex flex-wrap gap-1.5">
        {rule.patterns.map((p) => (
          <span
            key={p}
            className="rounded-pill bg-field px-2 py-0.5 text-[10.5px] font-medium text-text-2"
          >
            {p}
          </span>
        ))}
      </div>

      {rule.example ? (
        <p className="mt-2.5 rounded-lg border border-line bg-surface-2 px-3 py-2 text-[11.5px] italic leading-relaxed text-text-2">
          “{rule.example}”
        </p>
      ) : null}

      {/* Action — read-only (always a handoff for priority triggers). */}
      <div className="mt-2.5 flex items-start gap-1.5 text-[11.5px] text-text-2 [&_svg]:size-[13px]">
        <svg
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.3"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
          className="mt-px flex-none text-warn"
        >
          <path d="M3 12h4l2-5 4 10 2-5h6" />
        </svg>
        <span>
          <b className="font-semibold text-text">Acción:</b> {rule.action}
        </span>
      </div>

      {/* The one editable field: the crisis/ER redirect copy. */}
      <div className="mt-3">
        <FormField
          label="Mensaje de redirección"
          htmlFor={fieldId}
          helper="La copia que se muestra a la persona al activarse este disparador."
        >
          <Textarea
            id={fieldId}
            value={message}
            onChange={(e) => onMessageChange(e.target.value)}
            disabled={disabled}
            maxLength={1000}
            showCount
            placeholder="Ej. Si es una emergencia, llama al 911 o acude a urgencias."
          />
        </FormField>
      </div>
    </li>
  )
}

export function TemplateDetailPage(): JSX.Element {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { data: tpl, isLoading } = useQuery({
    queryKey: ['admin', 'template', id],
    queryFn: () => getVerticalTemplate(id),
  })
  const qc = useQueryClient()
  const [tab, setTab] = useState('sensibilidad')
  // Only the sensitivity editor is built; the other dimension editors are shown
  // as disabled tabs (an honest "not yet" signal) rather than clickable tabs
  // that open an empty "Próximamente" panel.
  const editorTabs = EDITOR_TABS.map((t) =>
    t.id === 'sensibilidad' ? t : { ...t, disabled: true },
  )
  const [rules, setRules] = useState<SensitivityRule[]>([])
  // Edited redirect messages, keyed by rule id. Only rules the operator touched
  // appear here; everything else falls back to the published message.
  const [drafts, setDrafts] = useState<Record<string, string>>({})
  const [publishing, setPublishing] = useState(false)
  // Duplicate-as-new + rename modals.
  const [dupOpen, setDupOpen] = useState(false)
  const [dupName, setDupName] = useState('')
  const [busy, setBusy] = useState(false)
  const [modalError, setModalError] = useState('')
  const [renameOpen, setRenameOpen] = useState(false)
  const [renName, setRenName] = useState('')

  useEffect(() => {
    if (tpl) {
      setRules(tpl.sensitivityRules)
      setDrafts({})
    }
  }, [tpl])

  async function submitDuplicate(): Promise<void> {
    setBusy(true)
    setModalError('')
    try {
      const created = await createVerticalTemplate({ name: dupName.trim(), sourceId: id })
      await qc.invalidateQueries({ queryKey: ['admin', 'templates'] })
      setDupOpen(false)
      void navigate(`/admin/templates/${created.id}`)
    } catch (err) {
      setModalError(apiErrorMessage(err, 'No se pudo duplicar la plantilla.'))
    } finally {
      setBusy(false)
    }
  }

  async function submitPublish(triggers: { id: string; message: string }[]): Promise<void> {
    setPublishing(true)
    try {
      const result = await publishTemplate(id, triggers)
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'template', id] }),
        qc.invalidateQueries({ queryKey: ['admin', 'templates'] }),
      ])
      setDrafts({})
      toast.ok(
        `Publicado. ${result.botsInvalidated} ${
          result.botsInvalidated === 1 ? 'agente actualizado' : 'agentes actualizados'
        }.`,
      )
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo publicar la plantilla.'))
    } finally {
      setPublishing(false)
    }
  }

  async function submitRename(): Promise<void> {
    setBusy(true)
    setModalError('')
    try {
      await renameVerticalTemplate(id, { name: renName.trim() })
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['admin', 'template', id] }),
        qc.invalidateQueries({ queryKey: ['admin', 'templates'] }),
      ])
      setRenameOpen(false)
      toast.ok('Plantilla renombrada')
    } catch (err) {
      setModalError(apiErrorMessage(err, 'No se pudo renombrar.'))
    } finally {
      setBusy(false)
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-4xl py-10">
        <Spinner label="Cargando plantilla…" />
      </div>
    )
  }

  if (!tpl) {
    return (
      <div className="mx-auto w-full max-w-2xl">
        <PageHeader title="Plantilla no encontrada" />
        <div className="mt-6">
          <EmptyStateCard
            icon={
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                aria-hidden
              >
                <path d="M3 7l9-4 9 4-9 4-9-4Z" strokeLinejoin="round" />
                <path d="M3 12l9 4 9-4" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
            title="No encontramos esta plantilla"
            body="Puede que el enlace sea incorrecto o que la plantilla haya cambiado de nombre."
            actions={
              <Button onClick={() => void navigate('/admin/templates')}>Volver a plantillas</Button>
            }
          />
        </div>
      </div>
    )
  }

  // Only rules whose redirect copy actually changed (and isn't blank) get sent.
  const dirtyTriggers = rules
    .filter((r) => drafts[r.id] !== undefined && drafts[r.id] !== r.message)
    .map((r) => ({ id: r.id, message: drafts[r.id]! }))
  const hasBlankEdit = dirtyTriggers.some((t) => t.message.trim() === '')
  const canPublish = dirtyTriggers.length > 0 && !hasBlankEdit && !publishing
  const currentVersionNum = Number.parseInt(tpl.version.replace(/^v/i, ''), 10)
  const nextVersionLabel = Number.isFinite(currentVersionNum)
    ? `v${currentVersionNum + 1}`
    : tpl.version

  return (
    <div data-testid="admin-template-detail" className="mx-auto w-full max-w-4xl">
      <PageHeader
        breadcrumb={[{ label: 'Plantillas verticales', href: '/admin/templates' }]}
        title={tpl.name}
      />

      <div className="mt-4 flex flex-wrap items-start gap-3">
        <span
          aria-hidden
          className={`grid size-12 flex-none place-items-center rounded-xl [&_svg]:size-[24px] ${VERTICAL_WRAP[tpl.vertical]}`}
        >
          <VerticalIcon vertical={tpl.vertical} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Chip tone="ok" dot>
              {tpl.version} publicada
            </Chip>
          </div>
          <p className="mt-1.5 text-[12px] text-text-2">
            {tpl.botsInheriting} agentes heredando · {tpl.editedMeta}
          </p>
        </div>
        <div className="flex flex-none items-center gap-2">
          <Button
            size="sm"
            onClick={() => void submitPublish(dirtyTriggers)}
            loading={publishing}
            disabled={!canPublish}
          >
            Publicar {nextVersionLabel}
          </Button>
          <DropdownMenu
            trigger={
              <button
                type="button"
                aria-label="Más acciones de la plantilla"
                className="flex size-8 flex-none items-center justify-center rounded-md border border-line text-text-3 transition-colors hover:bg-hover hover:text-text [&_svg]:size-[16px]"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <circle cx="12" cy="5" r="1.7" />
                  <circle cx="12" cy="12" r="1.7" />
                  <circle cx="12" cy="19" r="1.7" />
                </svg>
              </button>
            }
            items={[
              {
                label: 'Renombrar',
                onSelect: () => {
                  setRenName(tpl.name)
                  setModalError('')
                  setRenameOpen(true)
                },
              },
              {
                label: 'Duplicar plantilla',
                onSelect: () => {
                  setDupName(`${tpl.name} (copia)`)
                  setModalError('')
                  setDupOpen(true)
                },
              },
            ]}
          />
        </div>
      </div>

      <div className="mt-4">
        <Tabs
          tabs={editorTabs}
          activeId={tab}
          onChange={setTab}
          idBase="template-editor"
          aria-label="Dimensiones de la plantilla"
        />
      </div>

      <div className="mt-5">
        <TabPanel idBase="template-editor" tabId={tab}>
          {tab === 'sensibilidad' ? (
            rules.length === 0 ? (
              <EmptyStateCard
                icon={
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden
                  >
                    <path d="M3 12h4l2-5 4 10 2-5h6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                }
                title="Sin reglas de sensibilidad"
                body="Esta vertical no define reglas de sensibilidad clínica. Las reglas de cumplimiento viven en la pestaña Compliance."
              />
            ) : (
              <>
                <div className="mb-3 flex items-baseline justify-between gap-3">
                  <h2 className="text-[13.5px] font-bold text-text">Reglas de sensibilidad</h2>
                  <span className="text-[11.5px] text-text-3">
                    {rules.length} {rules.length === 1 ? 'regla' : 'reglas'} · siempre activas
                  </span>
                </div>
                <p className="mb-3 text-[11.5px] text-text-3">
                  Los disparadores y su acción son no negociables. Puedes editar el mensaje de
                  redirección y publicar para aplicarlo a todos los agentes de esta vertical.
                </p>
                <ul className="flex flex-col gap-2.5">
                  {rules.map((r) => (
                    <RuleCard
                      key={r.id}
                      rule={r}
                      message={drafts[r.id] ?? r.message}
                      onMessageChange={(value) => setDrafts((d) => ({ ...d, [r.id]: value }))}
                      disabled={publishing}
                    />
                  ))}
                </ul>
              </>
            )
          ) : (
            <EmptyStateCard
              icon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              }
              title="Próximamente"
              body="El editor de esta dimensión está en construcción."
            />
          )}
        </TabPanel>
      </div>

      <Modal
        open={dupOpen}
        onClose={() => setDupOpen(false)}
        title="Duplicar plantilla"
        subtitle="Crea una copia con todo lo de esta plantilla. Luego la ajustas."
        footer={
          <>
            <Button variant="secondary" size="sm" onClick={() => setDupOpen(false)} disabled={busy}>
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={() => void submitDuplicate()}
              loading={busy}
              disabled={dupName.trim() === ''}
            >
              Duplicar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField label="Nombre de la copia" htmlFor="dup-name">
            <Input id="dup-name" value={dupName} onChange={(e) => setDupName(e.target.value)} />
          </FormField>
          {modalError ? <p className="text-[12px] text-sens">{modalError}</p> : null}
        </div>
      </Modal>

      <Modal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        title="Renombrar plantilla"
        subtitle="Cómo se llama esta plantilla en el catálogo."
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setRenameOpen(false)}
              disabled={busy}
            >
              Cancelar
            </Button>
            <Button
              size="sm"
              onClick={() => void submitRename()}
              loading={busy}
              disabled={renName.trim() === ''}
            >
              Guardar
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <FormField label="Nombre" htmlFor="ren-name">
            <Input id="ren-name" value={renName} onChange={(e) => setRenName(e.target.value)} />
          </FormField>
          {modalError ? <p className="text-[12px] text-sens">{modalError}</p> : null}
        </div>
      </Modal>
    </div>
  )
}
